<template>
  <v-card class="notification-card d-flex flex-column" elevation="4">
    <div class="pa-10">
      <div v-if="player" class="small-event-player-card mb-2">
        <Avatar :id="player.id" class="event-avatar" />
        <span class="ml-2">
          <div class="player-name">
            {{ player.firstName }} {{ player.lastName }}
          </div>
          <div class="subtext">{{ event.orgUnit.name }}</div>
        </span>
        <v-icon color="white" style="font-size: 24px">mdi-chevron</v-icon>
      </div>
      <div v-if="!player" class="small-event-player-card mb-2">
        <span>
          <div class="player-name">
            {{ event.orgUnit.name }}
          </div>
        </span>
      </div>

      <div :class="{ expanded: expand }" class="event-details">
        <div class="event-date">
          {{ start.format("ddd MMM, DD") }}
        </div>

        <div class="event-time">
          {{ start.format("h:mm")
          }}<span class="event-ampm">{{ start.format("a") }}</span> -{{
            end.format("h:mm")
          }}<span class="event-ampm">{{ end.format("a") }}</span>
          <span class="event-timezone">{{ event.timezone }}</span>
        </div>

        <div class="mt-4">
          <span :style="{ color: event.eventType.color }" class="event-type">{{
            event.eventType.name
          }}</span>
        </div>

        <div class="event-name">
          {{ event.name }}
        </div>

        <div class="mt-0">
          <div class="subtext">Location</div>
          <a
            v-if="location.address"
            :href="location.mapUrl"
            class="mt-0 float-right"
            style="text-transform: none; letter-spacing: normal; border: 1px solid dodgerblue; padding: 7px 5px; border-radius: 5px;"
            target="teambot-map"
          >
            <v-icon size="16" style="color: dodgerblue;">mdi-map-marker</v-icon>
            Map
          </a>
          <div class="location-name">
            {{ location.name
            }}{{
              event.field
                ? ", " + event.orgUnit.sport.fieldLabel + " " + event.field
                : ""
            }}
          </div>
        </div>

        <div v-if="expand" class="d-flex justify-space-between">
          <div v-if="location.address" class="event-address">
            {{ location.address }}<br />
            {{ location.city }}, {{ location.state }}
            {{ location.zip }}
          </div>
          <div v-if="!location.address" class="event-address">
            No address provided
          </div>
        </div>

        <div v-if="expand && event.description" class="mt-6">
          <div class="subtext">Description</div>
          <div>
            {{ event.description }}
          </div>
        </div>

        <div v-if="player" class="mt-6">
          <div class="subtext">Note to Coach</div>
          <InlineNoteEditor
            v-if="invite"
            :invite="invite"
            style="margin-left: -4px; margin-bottom: 16px"
          />
        </div>

        <div v-if="player && expand">
          <div class="subtext">Confirmed</div>
          <div v-for="i in acceptedInvites" :key="i.id">
            <v-icon color="success" small>mdi-check-circle </v-icon>
            {{ i.player.firstName }} {{ i.player.lastName }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="player && invite" class="d-flex justify-space-between">
      <div :class="responseClasses" class="response-container">
        <div
          v-if="
            inviteStatus == 'Accepted' && !updatingAccepted && !updatingDeclined
          "
          class="pa-2 ml-3 response-yes"
          style="cursor: pointer"
          @click="_updateAvailability('Pending', null)"
        >
          <div class="response-text">Great, see you there!</div>
          <div v-if="!updatingPending" class="response-subtext">
            Change response
          </div>
          <v-progress-circular v-if="updatingPending" indeterminate size="24" />
        </div>
        <div
          v-if="
            inviteStatus == 'Declined' && !updatingAccepted && !updatingDeclined
          "
          class="pa-2 ml-3 response-no"
          style="cursor: pointer"
          @click="_updateAvailability('Pending', null)"
        >
          <div class="response-text">Sorry you can't make it!</div>
          <div v-if="!updatingPending" class="response-subtext">
            Change response
          </div>
          <v-progress-circular v-if="updatingPending" indeterminate size="24" />
        </div>
        <div
          v-if="inviteStatus == 'Pending'"
          class="d-flex"
          style="cursor: pointer; height: 100%"
        >
          <div
            class="d-flex align-center justify-center response-container-right flex"
            @click="updateAvailabilityNo"
          >
            <v-icon v-if="!updatingDeclined" class="response-icon"
              >mdi-emoticon-sad-outline
            </v-icon>
            <v-progress-circular
              v-if="updatingDeclined"
              indeterminate
              size="24"
            />
            <div class="response-text ml-3">Not Going</div>
          </div>
          <div></div>
          <div
            class="d-flex align-center justify-center response-container-left flex"
            @click="updateAvailabilityYes"
          >
            <v-icon v-if="!updatingAccepted" class="response-icon"
              >mdi-emoticon-excited
            </v-icon>
            <v-progress-circular
              v-if="updatingAccepted"
              indeterminate
              size="24"
            />
            <div class="response-text ml-3">Yes, Going</div>
          </div>
        </div>
      </div>
      <div
        v-if="!fullSize"
        class="chevron-down-container d-flex align-center pr-4 pl-4"
        style="cursor:pointer;"
        @click="expand = !expand"
      >
        <v-icon v-if="!expand" class="chevron" size="36"
          >mdi-chevron-down
        </v-icon>
        <v-icon v-if="expand" class="chevron" size="36">mdi-chevron-up</v-icon>
      </div>
    </div>
  </v-card>
</template>
<script>
import Vue from "vue";
import { updatePlayerAvailability } from "@/helpers/api";
import moment from "moment";
import { mapActions } from "vuex";
import { ModuleTypes } from "@/store/ModuleTypes";
import { SnackbarMutationTypes } from "@/views/academy/common/snackbar/SnackbarMutationTypes";
import { i18n } from "@/plugins/i18n";
import Avatar from "@/components/Avatar.vue";
import InlineNoteEditor from "@/components/common/InlineNoteEditor";

export default Vue.extend({
  name: "SmallEventCard",
  components: {
    Avatar,
    InlineNoteEditor
  },
  props: {
    location: {
      type: Object
    },
    player: {
      type: Object
    },
    invite: {
      type: Object
    },
    event: {
      type: Object
    },
    expanded: {
      type: Boolean
    },
    fullSize: {
      type: Boolean
    },
    light: {
      type: Boolean
    }
  },
  data() {
    return {
      eventStart: null,
      eventEnd: null,
      updatingAccepted: false,
      updatingDeclined: false,
      updatingPending: false,
      inviteStatus: null,
      note: null,
      newNote: "",
      editNote: false,
      savingNote: false,
      expand: false
    };
  },
  mounted() {
    this.inviteStatus = this.invite ? this.invite.status.value : null;
    this.note = this.invite ? this.invite.note : null;
    if (this.expanded || !this.player) this.expand = true;
  },
  watch: {
    invite: function(newVal) {
      this.inviteStatus = newVal ? newVal.status.value : null;
      this.note = newVal ? newVal.note : null;
    }
  },
  computed: {
    start() {
      return moment(this.event.start);
    },
    end() {
      return moment(this.event.end);
    },
    hasNote() {
      return this.note !== null && this.note.length > 0;
    },
    hasAddress() {
      return this.location.global !== true && this.location.address !== null;
    },
    showJoinVideoEvent() {
      return false;
      // ${event.videoEvent && event.getMillisUntilStart() < 300000 /* 5 minutes */  }
    },
    inviteAvailabilityClass() {
      return this.inviteStatus
        ? "player-invite-" + this.inviteStatus.toLowerCase()
        : "";
    },
    inviteYesClass() {
      return this.inviteStatus
        ? `yes-btn-${this.inviteStatus.toLowerCase()}`
        : "";
    },
    responseClasses() {
      let classes = "";
      classes +=
        this.inviteStatus === "Accepted"
          ? "tb-green"
          : this.inviteStatus === "Declined"
          ? "tb-red"
          : "";

      if (this.fullSize) {
        classes += " full-size";
      } else {
        classes += " regular-size";
      }

      return classes;
    },
    lightClass() {
      return this.light ? "notification-card-light" : "";
    },
    acceptedInvites() {
      return this.event.playerInvites.filter(
        invite => invite.status.value === "Accepted"
      );
    }
  },
  methods: {
    toggleNoteEditor() {
      this.editNote = !this.editNote;
    },
    saveNote(note) {
      this.savingNote = true;
      this._updateAvailability(this.inviteStatus, note)
        .then(() => {
          this.savingNote = false;
          this.toggleNoteEditor();
          this.SHOW_SNACKBAR({
            text: i18n.tc("snackbar_message.saved_successfully")
          });
        })
        .catch(() => {
          this.SHOW_SNACKBAR({
            text: i18n.tc("snackbar_message.save_failed"),
            color: "error"
          });
        });
    },
    updateAvailabilityYes() {
      this.updatingAccepted = true;
      this._updateAvailability("Accepted", null);
    },
    updateAvailabilityNo() {
      this.updatingDeclined = true;
      this._updateAvailability("Declined", null);
    },
    async _updateAvailability(inviteStatus, note) {
      this.updatingPending = true;
      const previousStatus = this.inviteStatus;

      let response;

      try {
        response = await updatePlayerAvailability(
          this.invite.id,
          inviteStatus,
          note
        );
        if (response.status === 200) {
          this.inviteStatus = inviteStatus;
          if (response.data.note !== null) this.note = note;
          this.updatingAccepted = false;
          this.updatingDeclined = false;
          this.updatingPending = false;
        } else if (response.status === 400) {
          this.inviteStatus = previousStatus;
          this.updatingAccepted = false;
          this.updatingDeclined = false;
          this.updatingPending = false;
          alert("Error updating availability.");
        }
      } catch (exc) {
        this.inviteStatus = previousStatus;
        this.updatingAccepted = false;
        this.updatingDeclined = false;
        this.updatingPending = false;
        alert("Error updating availability.");
      }
    },

    openMap() {
      window.open(this.location.mapUrl, "_blank");
    },

    joinEvent() {
      window.location = "/connect?eventId=" + this.event.id;
    },

    ...mapActions(ModuleTypes.SNACKBAR, {
      SHOW_SNACKBAR: SnackbarMutationTypes.SHOW_SNACKBAR
    })
  }
});
</script>
<style scoped>
.notification-card {
  padding: 0;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 15px;
}

.small-event-player-card {
  display: flex;
  align-items: center;
}

.event-details {
  height: 360px;
  overflow: hidden;
}

.event-details.expanded {
  height: auto;
}

.player-name {
  font-weight: bold;
  font-size: 18px;
}

.subtext {
  font-weight: 500;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.3);
}

.event-type {
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 20px;
}

.event-date {
  font-family: "Poppins", serif;
  font-size: 20px;
  letter-spacing: 0;
  line-height: 25px;
  padding: 0;
  margin: 15px 0 0 0;
  text-align: left;
}

.event-time {
  font-family: "Poppins", serif;
  font-size: 35px;
  line-height: 40px;
  letter-spacing: 0;
  padding: 0;
  margin: 0;
  text-align: left;
}

.event-ampm {
  font-family: "Poppins", serif;
  font-size: 16px;
  text-transform: uppercase;
  color: #ffffff;
  padding-top: 10px;
  text-align: left;
}

.event-timezone {
  font-family: "Poppins", serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.5);
  padding-top: 10px;
  margin-left: 4px;
}

.event-name {
  color: inherit;
  min-height: 65px;
  font-size: 22px;
  font-weight: BOLD;
  letter-spacing: 0;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.event-address {
  color: inherit;
  font-size: 18px;
  letter-spacing: 0;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.response-yes .event-name,
.response-no .event-name,
.response-yes .subtext,
.response-no .subtext {
  color: white;
}

.location-name {
  color: inherit;
  font-size: 22px;
  min-height: 65px;
  font-weight: BOLD;
  letter-spacing: 0;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.chevron-down-container {
  border-top: 2px solid var(--tb-dark-blue);
  border-left: 1px solid var(--tb-dark-blue);
  border-bottom-right-radius: 20px;
}

.notification-card-light .chevron-down-container {
  border-color: #dadada;
}

.response-container {
  flex: 1;
  border-top: 2px solid var(--tb-dark-blue);
  border-right: 1px solid var(--tb-dark-blue);
  height: 62px;
}

.notification-card-light .response-container {
  border-color: #dadada;
}

.notification-card-light .tb-red {
  background-color: #c50e2a;
}

.notification-card-light .tb-green {
  background-color: #099950;
}

.tb-red {
  background-color: var(--tb-red);
  border-bottom-left-radius: 20px;
}

.tb-green {
  background-color: var(--tb-green);
  border-bottom-left-radius: 20px;
}

.response-icon {
  color: inherit;
  opacity: 0.5;
}

.response-text {
  color: inherit;
  font-size: 1.2em;
  font-weight: bold;
}

.response-container-left {
  border-left: 1px solid var(--tb-dark-blue);
}

.response-container-right {
  border-right: 1px solid var(--tb-dark-blue);
  border-bottom-left-radius: 20px;
}

.response-container-right:hover,
.response-container-left:hover,
.response-yes:hover,
.response-no:hover,
.chevron-down-container:hover {
  transition: 0.3s;
}

.response-container-right:hover,
.response-container-left:hover,
.chevron-down-container:hover {
  background: rgb(54, 68, 80);
  box-shadow: 0 65px 60px 0 rgba(85, 109, 141, 0.05);
  transition: 0.3s;
}

.notification-card-light .response-container-left,
.notification-card-light .response-container-right {
  border-color: #dadada;
}

.notification-card-light .response-container-right:hover,
.notification-card-light .response-container-left:hover,
.notification-card-light .chevron-down-container:hover {
  background: #dadada;
}

.notification-card-light .chevron-down-container .chevron {
  color: inherit;
}

.chevron-down-container .chevron {
  color: white;
}

.full-size {
  border-bottom-right-radius: 20px;
  border-right: 0px;
}

.full-size .response-container-left {
  border-bottom-right-radius: 20px;
}
</style>
